/**
 * iframe artifact 안에서 돌아갈 preamble. srcDoc 맨 앞에 `<script>...</script>` 로 주입된다.
 *
 * 역할:
 * - `window.artifact.<method>(params)` 를 promise 기반 API 로 노출
 * - `window.parent.postMessage({ reqId, method, params }, '*')` 로 부모에게 RPC
 * - 부모가 `{ reqId, ok, data | error }` 로 응답하면 해당 promise resolve/reject
 * - 30초 타임아웃
 *
 * 보안: iframe sandbox 가 `allow-scripts` 만 있고 `allow-same-origin` 이 없으므로
 * 부모 DOM / 쿠키 / localStorage 접근 불가. 오직 postMessage 채널만 탈출구.
 * 부모는 method 를 allowlist 로 게이팅하고 기존 skill 권한 모델로 실행.
 */

/**
 * `query` / `excelQuery` 는 의도적으로 제외 — raw SQL 이 artifact srcdoc 에 인라인 되는
 * 경로를 원천 차단. 대신 서버에 저장된 SQL 의 `queryId` 로만 실행 가능한
 * `runSavedQuery` 를 노출한다. 원문 SQL 은 관리자의 saved_queries 감사 페이지에서만 조회.
 */
const ALLOWED_METHODS = [
  'runSavedQuery',
  'listTables',
  'describeTable',
  'excelList',
  'excelDescribe',
  'excelHead',
] as const;

export function buildBridgePreamble(): string {
  const methodsJson = JSON.stringify(ALLOWED_METHODS);
  // 주의: 이 스크립트는 iframe srcDoc 안에서 실행됨. 외부 문자열 보간 금지 (XSS 방지).
  // methodsJson 은 const array 를 JSON.stringify 한 것이라 안전.
  return `<script>
(function(){
  var pending = new Map();
  var nextId = 0;
  window.addEventListener('message', function(ev){
    if (ev.source !== window.parent) return;
    var d = ev.data;
    if (!d || typeof d !== 'object' || typeof d.reqId !== 'number') return;
    var r = pending.get(d.reqId);
    if (!r) return;
    pending.delete(d.reqId);
    if (d.ok) r.resolve(d.data); else r.reject(new Error(d.error || 'RPC error'));
  });
  function call(method, params){
    var reqId = ++nextId;
    return new Promise(function(resolve, reject){
      pending.set(reqId, { resolve: resolve, reject: reject });
      window.parent.postMessage({ reqId: reqId, method: method, params: params || {} }, '*');
      setTimeout(function(){
        if (pending.has(reqId)) { pending.delete(reqId); reject(new Error('artifact RPC timeout (30s)')); }
      }, 30000);
    });
  }
  var methods = ${methodsJson};
  var api = {};
  methods.forEach(function(m){ api[m] = function(p){ return call(m, p); }; });
  Object.defineProperty(window, 'artifact', { value: api, writable: false, configurable: false });
})();
</script>
`;
}
